import { useEffect, useMemo, useState } from 'react';
import { methodDateAvailability, type PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { useTranslation } from '@/hooks/useTranslation';

export interface PlanMethodAvailability {
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: (method: PaymentMethod) => void;
  methodDisabledReasons: Partial<Record<PaymentMethod, string>>;
}

/**
 * Owns payment-method selection for a plan tab: which of `availablePlanMethods`
 * clear their lead-time floor for `startDate`, the reason text for ones that
 * don't yet, and reconciling the current selection whenever the qualifying set
 * changes under it (e.g. the date moves, or a method drops out of
 * `availablePlanMethods`).
 */
export function usePlanMethodAvailability(
  availablePlanMethods: PaymentMethod[],
  startDate: Date | undefined,
  /**
   * The method the customer already chose, when they are coming back to a plan they configured
   * — "Edit plan" remounts the tab, and without this their choice would silently revert to
   * whichever method the operator happens to list first.
   */
  chosenMethod?: PaymentMethod | null
): PlanMethodAvailability {
  const { t, currentLanguage } = useTranslation();
  const today = new Date();
  // methodDateAvailability anchors its own "today" to the UTC calendar day (see
  // payment-methods.ts), so that's the key that should invalidate this memo — a
  // tab left open across UTC midnight must recompute, not keep checking against
  // a stale mount-time date.
  const todayUtcDayKey = today.toISOString().slice(0, 10);

  const methodAvailability = useMemo(
    () => methodDateAvailability(availablePlanMethods, startDate ?? today, today),
    [availablePlanMethods, startDate, todayUtcDayKey]
  );
  const selectableMethods = useMemo(
    () => availablePlanMethods.filter((m) => methodAvailability[m]?.available ?? true),
    [availablePlanMethods, methodAvailability]
  );
  // Seeded from selectableMethods, not availablePlanMethods, so a lead-time-gated
  // method listed first never renders checked-but-disabled for an initial frame. A method the
  // customer already chose wins, provided it still clears its lead time for this date.
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    () => (chosenMethod && selectableMethods.includes(chosenMethod) ? chosenMethod : selectableMethods[0]) ?? null
  );

  useEffect(() => {
    setPaymentMethod((prev) => {
      if (selectableMethods.length === 0) return null;
      if (prev !== null && selectableMethods.includes(prev)) return prev;
      return selectableMethods[0];
    });
  }, [selectableMethods]);

  // Reasons for methods still in the list but not yet selectable — shown
  // inline on their own row by PaymentMethodSelectionSimple.
  const methodDisabledReasons = useMemo(() => {
    const reasons: Partial<Record<PaymentMethod, string>> = {};
    for (const m of availablePlanMethods) {
      const a = methodAvailability[m];
      if (a && !a.available && a.availableFrom) {
        reasons[m] = t('paymentMethod.unavailableUntilDate', {
          date: a.availableFrom.toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric' }),
        });
      }
    }
    return reasons;
  }, [availablePlanMethods, methodAvailability, t, currentLanguage]);

  return { paymentMethod, setPaymentMethod, methodDisabledReasons };
}
